import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { CommentCard } from "@/features/study-group-stream/components/CommentCard";
import {
  CommentThread,
  type CommentWithVisibility,
} from "@/features/study-group-stream/components/CommentThread";
import { useCommentHighlighting } from "@/features/study-group-stream/hooks/useCommentHighlighting";
import { useCommentSectionViewState } from "@/features/study-group-stream/hooks/useCommentSectionViewState";
import {
  type UseCommentSectionProps,
  useCommentSection,
} from "@/features/study-group-stream/hooks/useCommentSection";
import {
  useCreatedCommentScroll,
  useReplyToggleReveal,
  useRevealLoadMoreAfterCollapse,
} from "@/features/study-group-stream/hooks/useCommentSectionUiEffects";
import {
  COMMENT_LIST_GRID_CLASS,
  LINK_BUTTON_CLASS,
  REPLY_INPUT_ALIGN_CLASS,
} from "@/features/study-group-stream/utils/commentSectionLayout";
import { useConfirmDeleteDialog } from "@/hooks/useConfirmDeleteDialog";
import { isUserAuthor } from "../utils/commentSectionUtils";

interface CommentSectionProps extends UseCommentSectionProps {
  onSendMessage?: (userId: number) => void;
  openComposer?: boolean;
  onCommentSent?: () => void;
  highlightCommentId?: number | null;
  isArchived?: boolean;
  highlightCommentIds?: number[];
  shouldScrollToComment?: boolean;
}

export function CommentSection({
  postContentId,
  postObjectId,
  studyGroupId,
  selectedTopics,
  currentUser,
  onSendMessage,
  openComposer = false,
  onCommentSent,
  highlightCommentId = null,
  isArchived = false,
  highlightCommentIds: providedHighlightCommentIds = [],
  shouldScrollToComment = true,
}: CommentSectionProps) {
  const { t } = useTranslation();
  const highlightCommentIds = useMemo(() => {
    const candidates =
      providedHighlightCommentIds.length > 0
        ? providedHighlightCommentIds
        : highlightCommentId == null
          ? []
          : [highlightCommentId];
    const uniqueIds: number[] = [];
    const seen = new Set<number>();

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isNaN(parsed) || seen.has(parsed)) continue;
      seen.add(parsed);
      uniqueIds.push(parsed);
    }

    return uniqueIds;
  }, [providedHighlightCommentIds, highlightCommentId]);
  const highlightedCommentIds = useMemo(
    () => new Set(highlightCommentIds),
    [highlightCommentIds],
  );
  const {
    deleteTargetId,
    setDeleteTargetId,
    createdCommentTarget,
    setCreatedComment,
    clearCreatedCommentTarget,
    shouldRevealLoadMoreAfterCollapse,
    requestRevealLoadMoreAfterCollapse,
    clearRevealLoadMoreAfterCollapse,
    replyToggleScrollTargetId,
    requestReplyToggleScroll,
    clearReplyToggleScrollTarget,
    loadMoreControlsRef,
  } = useCommentSectionViewState();
  const deleteDialog = useConfirmDeleteDialog();

  const {
    comments,
    allComments,
    totalComments,
    hasMoreComments,
    isExpanded,
    isLoadingMoreComments,
    replyCtx,
    isSubmitting,
    handleLoadMoreComments,
    handleCollapseComments,
    expandAllComments,
    expandRepliesForComment,
    handleToggleReplies,
    handleReplyClick,
    handleClearReplyCtx,
    handleSubmitComment,
    handleDeleteComment,
    handleEditComment,
    handleReactComment,
    isDeleting,
  } = useCommentSection({
    postContentId,
    postObjectId,
    studyGroupId,
    selectedTopics,
    currentUser,
    onCommentSubmitted: ({ createdCommentId }, { objectId }) => {
      if (createdCommentId) {
        setCreatedComment(createdCommentId);
      }
      if (objectId === postObjectId) {
        onCommentSent?.();
      }
    },
  });
  const hasComments = comments.length > 0;
  const handleDeleteRequest = useCallback(
    async (id: number) => {
      setDeleteTargetId(id);
      deleteDialog.open();
    },
    [deleteDialog, setDeleteTargetId],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (deleteTargetId === null) return;
    const id = deleteTargetId;
    try {
      await handleDeleteComment(id);
    } catch (error) {
      console.error("[CommentSection] delete request failed", { id, error });
      alert(t("common.deleteError"));
    } finally {
      setDeleteTargetId(null);
      deleteDialog.close();
    }
  }, [deleteDialog, deleteTargetId, handleDeleteComment, setDeleteTargetId, t]);

  const isReplyMode = Boolean(replyCtx);
  const isTopComposerOpen = openComposer && !replyCtx;

  useEffect(() => {
    if (openComposer && replyCtx) {
      handleClearReplyCtx();
    }
  }, [openComposer, replyCtx, handleClearReplyCtx]);

  useCreatedCommentScroll({
    createdCommentTarget,
    clearCreatedCommentTarget,
    hasMoreComments,
    isLoadingMoreComments,
    handleLoadMoreComments,
  });

  const handleCollapseAndRevealLoadMore = useCallback(() => {
    handleCollapseComments();
    requestRevealLoadMoreAfterCollapse();
  }, [handleCollapseComments, requestRevealLoadMoreAfterCollapse]);

  useRevealLoadMoreAfterCollapse({
    shouldRevealLoadMoreAfterCollapse,
    clearRevealLoadMoreAfterCollapse,
    isExpanded,
    hasMoreComments,
    hasCreatedCommentTarget: createdCommentTarget !== null,
    loadMoreControlsRef,
  });

  useReplyToggleReveal({
    replyToggleScrollTargetId,
    clearReplyToggleScrollTargetId: clearReplyToggleScrollTarget,
  });

  const handleToggleRepliesAndRevealShowMore = useCallback(
    (commentId: number, areRepliesExpanded: boolean) => {
      handleToggleReplies(commentId);
      if (areRepliesExpanded) {
        requestReplyToggleScroll(commentId);
      }
    },
    [handleToggleReplies, requestReplyToggleScroll],
  );

  const renderReplyInput = (
    parentId: number,
    anchorId: number,
    variant: "comment" | "reply",
  ) => {
    if (!isReplyMode) return null;
    if (replyCtx?.anchorId !== anchorId) return null;
    const containerClassName =
      variant === "comment"
        ? `mt-2 mb-2 min-w-0 ${REPLY_INPUT_ALIGN_CLASS}`
        : `mt-1 min-w-0 ${REPLY_INPUT_ALIGN_CLASS}`;

    return (
      <div className={containerClassName}>
        <CommentCard
          isInput
          postId={parentId} // parentId in API
          onAddComment={handleSubmitComment}
          isSubmitting={isSubmitting}
          isReplyInput
          autoFocus
          studyGroupId={studyGroupId}
        />
      </div>
    );
  };

  useCommentHighlighting({
    highlightCommentIds,
    allComments,
    isExpanded,
    hasMoreComments,
    isLoadingMoreComments,
    handleLoadMoreComments,
    expandAllComments,
    expandRepliesForComment,
    shouldScrollToComment,
  });

  return (
    <div className="flex flex-col px-3 pb-3 pt-0">
      <ConfirmationDialog
        open={deleteDialog.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            deleteDialog.close();
            setDeleteTargetId(null);
          }
        }}
        title={t("common.delete")}
        description={t("common.confirmDelete")}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
        variant="destructive"
      />

      {/* 1. 新規コメント入力 */}
      {!isArchived && isTopComposerOpen && (
        <div className={`mb-3 ${REPLY_INPUT_ALIGN_CLASS}`}>
          <CommentCard
            isInput
            postId={postObjectId}
            onAddComment={handleSubmitComment}
            isSubmitting={isSubmitting}
            autoFocus
            studyGroupId={studyGroupId}
          />
        </div>
      )}

      {/* 2. コメントリスト */}
      <div className={hasComments ? COMMENT_LIST_GRID_CLASS : "min-w-0"}>
        {hasComments && <div aria-hidden="true" />}
        <div className="min-w-0">
          {comments.map((comment) => (
            <CommentThread
              key={comment.id}
              comment={comment as CommentWithVisibility}
              replyCtx={replyCtx}
              highlightedCommentIds={highlightedCommentIds}
              onReaction={handleReactComment}
              onReplyClick={handleReplyClick}
              onDeleteComment={handleDeleteRequest}
              onEditComment={handleEditComment}
              onToggleReplies={handleToggleRepliesAndRevealShowMore}
              isCurrentUserAuthor={(target) =>
                isUserAuthor(target, currentUser?.id)
              }
              currentUserId={currentUser?.id}
              onSendMessage={onSendMessage}
              renderReplyInput={renderReplyInput}
              renderHiddenRepliesLabel={(count, areRepliesExpanded) =>
                areRepliesExpanded
                  ? t("common.showLess")
                  : `${t("common.showMore")} (${count})`
              }
              isArchived={isArchived}
            />
          ))}

          {/* 3. トップレベルコメントをさらに表示 */}
          {(hasMoreComments || isExpanded) && (
            <div ref={loadMoreControlsRef} className="flex flex-wrap gap-2">
              {hasMoreComments && (
                <Button
                  variant="ghost"
                  size="sm"
                  className={LINK_BUTTON_CLASS}
                  onClick={handleLoadMoreComments}
                  disabled={isLoadingMoreComments}
                >
                  {isLoadingMoreComments ? (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  ) : (
                    t("common.showMore")
                  )}{" "}
                  ({totalComments - comments.length})
                </Button>
              )}
              {isExpanded && (
                <Button
                  variant="ghost"
                  size="sm"
                  className={LINK_BUTTON_CLASS}
                  onClick={handleCollapseAndRevealLoadMore}
                >
                  {t("common.showLess")}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
